import React, { MouseEvent, useCallback, ReactElement, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { APP_STATUS, CONFLICT } from 'config/config';
import { AuthorizationType, availablePmpt, BlikErrorCodes, PaymentCategoryType } from 'consts';
import withLocaleContext from 'hoc/withLocaleContext';
import withContext from 'hoc/withContext';
import { updatePayment } from 'services/Api';
import { IError, PaymentPartial } from 'types/Payment';
import withSaferPayScriptContext from '../../../hoc/WithSaferPayScriptContext';
import withSelectedMethodContext from '../../../hoc/withSelectedMethodContext';

import { Container } from './PaymentButton.styles';
import { useAggregatedMetrics } from '../../../services/AggregatedMetricsContext';
import { ClientMetricsType } from '../../../services/ClientMetricsContext';
import { ISelectedMethodContext } from '../../../types/SelectedMethodContext';
import { ISaferPayScriptContext } from '../../../types/SaferPayScriptContext';

function redirectUser(redirectUrl: string) {
  window.location.href = redirectUrl;
}
type Props = RouteComponentProps &
  Partial<IContext> &
  Partial<ISaferPayScriptContext> &
  Partial<ILocaleContext> &
  Partial<ClientMetricsType> &
  Partial<ISelectedMethodContext> & {
    isVisible: boolean;
    payment: PaymentPartial;
    paymentId: string;
    paymentMethodId?: string;
    paymentMethodType?: string;
    showPaymentNotification?: boolean;
    setOrderDescriptionVisibility?: (visible: boolean) => void;
    showPaymentRedirectScreen?: () => void;
    setErrors?: (errors: IError[]) => void;
    setLoading: (isLoading: boolean) => void;
    handleError: (error: Error) => void;
    handleProcessedPayment: (isLoading: boolean) => void;
    isSaveInstrumentChecked: boolean;
    defaultRedirectUrl?: string;
    innerRef: RefObject<HTMLButtonElement>;
  };

function PaymentButton(props: Props): ReactElement {
  const {
    locale,
    paymentId,
    payment,
    paymentMethodId,
    paymentMethodType,
    setLoading,
    setErrors,
    handleError,
    handleProcessedPayment,
    setOrderDescriptionVisibility,
    showPaymentRedirectScreen,
    showPaymentNotification,
    isSelectedPaymentMethodDisabled,
    isSaveInstrumentChecked,
    isTokenizedPayment,
    formData = {},
    history,
    location,
    pmpt,
    authorizationType,
    defaultRedirectUrl,
    innerRef,
  } = props;
  const { sendMetrics } = useAggregatedMetrics();

  const handleBlik = async (selectedPaymentMethod: string, selectedPaymentMethodPType?: number) => {
    const { authorizationCode } = formData;
    const { status } = await updatePayment(paymentId, {
      authorizationCode,
      locale,
      paymentMethodId: selectedPaymentMethod,
      pmpt: selectedPaymentMethodPType,
    });

    if (setOrderDescriptionVisibility) setOrderDescriptionVisibility(false);
    history.push(`${location.pathname}/callback${location.search}&selectedPaymentMethodType=BLIK`, { status });
    handleProcessedPayment(false);
  };

  const handleTokenizedCard = async (selectedPaymentMethod: string, selectedPaymentMethodPType: number) => {
    const { paymentMethodToken } = formData;
    const data = await updatePayment(paymentId, {
      locale,
      paymentMethodId: selectedPaymentMethod,
      paymentMethodToken,
      pmpt: selectedPaymentMethodPType,
      tokenizedPayment: isTokenizedPayment,
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    redirectUser(data.redirectUrl);
  };

  const handleWorldlineCard = async (selectedPaymentMethod: string, selectedPaymentMethodPType: number) => {
    const { saferPayFields, saferPayToken } = props;

    if (saferPayToken) {
      const { redirectUrl } = await updatePayment(paymentId, {
        locale,
        paymentMethodId: selectedPaymentMethod,
        paymentMethodToken: saferPayToken,
        pmpt: selectedPaymentMethodPType,
        saveInstrument: isSaveInstrumentChecked,
      });
      redirectUser(redirectUrl);
    } else if (saferPayFields) {
      /* eslint-disable */
      // @ts-ignore
      saferPayFields.submit({
        onSuccess(evt: any) {
          const worldlineToken = evt.token;
          const metricsPromise = sendMetrics?.(paymentId) ?? Promise.resolve();

          metricsPromise
            .catch(() => {})
            .finally(() => {
              updatePayment(paymentId, {
                locale,
                paymentMethodId: selectedPaymentMethod,
                paymentMethodToken: worldlineToken,
                pmpt: selectedPaymentMethodPType,
                saveInstrument: isSaveInstrumentChecked,
              })
                .then(data => redirectUser(data.redirectUrl))
                .catch(() => {
                  redirectUser(`${paymentId}/callback${location.search}`);
                });
            });
        },
        onError() {
          redirectUser(`${paymentId}/init-failed${location.search}`);
        },
      });
    }
  };

  const handleBluemediaCard = async (selectedPaymentMethod: string, selectedPaymentMethodPType?: number) => {
    const { paymentMethodToken } = formData;
    const { redirectUrl } = await updatePayment(paymentId, {
      locale,
      paymentMethodId: selectedPaymentMethod,
      paymentMethodToken,
      pmpt: selectedPaymentMethodPType,
    });
    redirectUser(redirectUrl);
  };

  const handleOtherPayment = async (selectedPaymentMethod: string, selectedPaymentMethodPType?: number) => {
    const { redirectUrl, status } = await updatePayment(paymentId, {
      locale,
      paymentMethodId: selectedPaymentMethod,
      pmpt: selectedPaymentMethodPType,
    });

    if (![APP_STATUS.NEW, APP_STATUS.PENDING].includes(status)) {
      setLoading(false);
      history.push(`${location.pathname}/callback${location.search}`, { status });
    } else {
      redirectUser(redirectUrl);
    }
  };

  const handleClick = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation();
      const selectedPaymentMethodType = paymentMethodType || payment.selectedPaymentMethodType;
      const selectedShowPaymentNotification =
        showPaymentNotification ||
        (payment.selectedShowPaymentNotification && isSelectedPaymentMethodDisabled === false);

      if (
        PaymentCategoryType.PBL === selectedPaymentMethodType &&
        selectedShowPaymentNotification &&
        showPaymentRedirectScreen
      ) {
        showPaymentRedirectScreen();
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      if (defaultRedirectUrl) {
        window.location.assign(defaultRedirectUrl);
        return;
      }

      try {
        setLoading(true);
        if (setErrors) setErrors([]);

        const selectedPaymentMethod = paymentMethodId || payment.selectedPaymentMethodId;
        const selectedPaymentMethodPType = pmpt || payment.selectedPmpt;

        if (!selectedPaymentMethod) {
          throw new Error('Payment method not selected');
        }

        if (PaymentCategoryType.BLIK === selectedPaymentMethodType && authorizationType === AuthorizationType.CODE) {
          await handleBlik(selectedPaymentMethod, selectedPaymentMethodPType);
        } else if (
          PaymentCategoryType.CARD === selectedPaymentMethodType ||
          PaymentCategoryType.CARD_RECURRING === selectedPaymentMethodType
        ) {
          if (selectedPaymentMethodPType === availablePmpt.WORLDLINE) {
            if (isTokenizedPayment) {
              await handleTokenizedCard(selectedPaymentMethod, selectedPaymentMethodPType);
            } else {
              await handleWorldlineCard(selectedPaymentMethod, selectedPaymentMethodPType);
            }
          } else {
            await handleBluemediaCard(selectedPaymentMethod, selectedPaymentMethodPType);
          }
        } else {
          await handleOtherPayment(selectedPaymentMethod, selectedPaymentMethodPType);
        }
      } catch (error) {
        const { statusCode, errors = [] } = error;
        let isConflict = false;
        let isBlikError = false;

        errors.forEach((err: { errorType: string }) => {
          if (err.errorType === CONFLICT) isConflict = true;
          if (Object.keys(BlikErrorCodes).includes(err.errorType)) isBlikError = true;
        });

        if (statusCode === 400 && setErrors) {
          if (isConflict) {
            window.location.reload();
            return;
          }
          if (isBlikError) {
            setLoading(false);
            setErrors(errors);
            return;
          }
        }

        if (handleError) {
          setLoading(false);
          handleError(error);
        }
        throw error;
      }
    },
    [
      paymentMethodType,
      payment,
      defaultRedirectUrl,
      showPaymentRedirectScreen,
      showPaymentNotification,
      isSelectedPaymentMethodDisabled,
      paymentMethodId,
      pmpt,
      authorizationType,
      setLoading,
      setErrors,
      handleError,
      isTokenizedPayment,
      isSaveInstrumentChecked,
    ]
  );

  const messageId =
    paymentMethodType === PaymentCategoryType.PAYPO
      ? 'pay.continue.paypo'
      : defaultRedirectUrl
      ? 'pay.continue'
      : 'pay.text';

  return (
    <Container className="payment-button__container" isVisible={props.isVisible} onClick={handleClick} ref={innerRef}>
      <FormattedMessage id={messageId} />
    </Container>
  );
}

PaymentButton.defaultProps = {
  showPaymentNotification: false,
  isSelectedPaymentMethodDisabled: false,
  isTokenizedPayment: false,
} as Partial<Props>;

export default withLocaleContext(
  withSaferPayScriptContext(withContext(withSelectedMethodContext(withRouter(PaymentButton))))
);
